import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Calendar, Loader2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SchedulePage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch(`${API}/locations`);
        const data = await response.json();
        setLocations(data);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="schedule-page">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-r from-sip-cyan to-sip-cyan/80 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <MapPin className="w-16 h-16 mx-auto mb-4 text-white" />
            <h1 className="font-script text-4xl md:text-6xl text-white mb-4">
              Find Us
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Check out where we'll be serving up good vibes this week!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Schedule List */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20" data-testid="loading-spinner">
              <Loader2 className="w-12 h-12 text-sip-purple animate-spin mx-auto" />
            </div>
          ) : locations.length === 0 ? (
            <div className="text-center py-20" data-testid="no-locations">
              <div className="card-graffiti inline-block">
                <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-xl text-gray-600">
                  No upcoming locations scheduled yet.
                </p>
                <p className="text-gray-500 mt-2">
                  Check back soon!
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {locations.map((location, index) => (
                <motion.div
                  key={location.location_id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`card-graffiti ${index === 0 ? 'border-2 border-sip-purple' : ''}`}
                  data-testid={`location-card-${index}`}
                >
                  {index === 0 && (
                    <div className="inline-block bg-sip-purple text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                      Next Stop!
                    </div>
                  )}
                  
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className={`${index === 0 ? 'bg-sip-purple' : 'bg-sip-cyan'} w-20 h-20 rounded-2xl flex flex-col items-center justify-center shrink-0`}>
                      <Calendar className="text-white" size={24} />
                      <span className="text-white text-sm font-semibold mt-1">
                        {location.date.split('-')[2]}
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-semibold text-xl text-gray-900 mb-1">{location.name}</h3>
                      <p className="text-gray-600 flex items-center gap-2">
                        <MapPin size={16} className="text-sip-purple" /> {location.address}
                      </p>
                      <p className="text-gray-600 flex items-center gap-2 mt-1">
                        <Clock size={16} className="text-sip-cyan" /> {location.time_start} - {location.time_end}
                      </p>
                      {location.description && (
                        <p className="text-gray-500 mt-2 italic">
                          {location.description}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SchedulePage;
